import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Error404 from './pages/Error404';
import ScrollToTop from './utils/ScrollToTop';
import Faq from './pages/Faq';
import Top from './pages/top/Top';
import Login from './pages/login/Login';
import Settings from './pages/Settings';
import Desk from './pages/desk/Desk';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfSale from './pages/TermsOfSale';
import Terms from './pages/Terms';
import MessageForm from './pages/MessageForm';
import Plans from './pages/plans/Plans';
import Analysis from './pages/Analysis';
import CheckoutBasicSuccess from './pages/checkout/basic/success/checkoutBasicSuccess';
import CheckoutBasicCancel from './pages/checkout/basic/cancel/checkoutBasicCancel';
// 記事ページ
import ArticleLayout from './layouts/articles/ArticleLayout';
import { UserProvider } from './hooks/UserContext';
import AuthCallback from './pages/AuthCallback';
import { saveRefFromCurrentUrl } from './utils/referral';

const App: React.FC = () => {
    const { t, i18n } = useTranslation('common');
    useEffect(() => {
        // 初回レンダリング時にクエリのrefをCookieへ保存
        saveRefFromCurrentUrl();
    }, []);

    return (
        <HelmetProvider>
            <Router>
                <Helmet htmlAttributes={{ lang: i18n.language }}>
                    {/* PWA/OS連携用 アプリ名をロケールに合わせて設定 */}
                    <meta name="apple-mobile-web-app-title" content={t('appName')} />
                    <meta name="application-name" content={t('appName')} />
                    {/* 言語別のWeb App Manifest を参照 */}
                    <link
                        rel="manifest"
                        href={
                            i18n.language?.startsWith('ja')
                                ? '/manifest.ja.json'
                                : '/manifest.en.json'
                        }
                    />
                </Helmet>
                <ScrollToTop />
                <Routes>
                    {/**
                     * 公開ページ
                     */}
                    <Route path="/" element={<Top />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/terms-of-sale" element={<TermsOfSale />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                    <Route path="/message-form" element={<MessageForm />} />
                    <Route path="/plans" element={<Plans />} />
                    {/* 記事ページ（プレビュー機能にも対応） */}
                    <Route
                        path="/en/articles/:slug"
                        element={<ArticleLayout defaultLanguage="en" />}
                    />
                    <Route
                        path="/articles/:slug"
                        element={<ArticleLayout defaultLanguage="ja" />}
                    />

                    {/* エラーページ */}
                    <Route path="*" element={<Error404 />} />

                    {/**
                     * 公開 or 要認証ページ
                     */}
                    {/* 決済関連ページ（ログインユーザーの場合はユーザー情報取得する） */}
                    <Route
                        path="/checkout/basic/success"
                        element={
                            <UserProvider>
                                <CheckoutBasicSuccess />
                            </UserProvider>
                        }
                    />
                    <Route
                        path="/checkout/basic/cancel"
                        element={
                            <UserProvider>
                                <CheckoutBasicCancel />
                            </UserProvider>
                        }
                    />

                    {/**
                     * 要認証ページ
                     */}
                    <Route path="/auth-callback" element={<AuthCallback />} />
                    <Route
                        path="/desk"
                        element={
                            <UserProvider>
                                <Desk />
                            </UserProvider>
                        }
                    />
                    <Route
                        path="/analysis"
                        element={
                            <UserProvider>
                                <Analysis />
                            </UserProvider>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <UserProvider>
                                <Settings />
                            </UserProvider>
                        }
                    />
                </Routes>
            </Router>
        </HelmetProvider>
    );
};

export default App;
